import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Car, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { formatApiErrorDetail, fileUrl } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const { settings } = useSettings() || {};
  const logo = settings?.logo_image ? fileUrl(settings.logo_image) : null;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          {logo ? (
            <img src={logo} alt="Erskine Auto logo" className="h-12 w-auto max-w-[220px] object-contain" data-testid="login-logo-image" />
          ) : (
            <>
              <span className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center text-white">
                <Car size={22} />
              </span>
              <span className="font-display font-extrabold text-xl text-white">Erskine Auto</span>
            </>
          )}
        </Link>
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
          <div className="flex items-center gap-2 text-white mb-6">
            <Lock size={18} className="text-red-500" />
            <h1 className="font-display font-bold text-lg">Admin Login</h1>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-400">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-slate-900 border-slate-700 text-white" placeholder="you@email.com" data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-400">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-slate-900 border-slate-700 text-white" placeholder="••••••••" data-testid="admin-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-11 rounded-full active:scale-95" data-testid="admin-login-submit-button">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
            </Button>
          </form>
        </div>
        <Link to="/" className="block text-center text-slate-500 text-sm mt-6 hover:text-slate-300">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
